import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleExclamation,
  faCircleCheck,
  faCircleInfo,
  faClockRotateLeft,
  faChevronUp,
  faChevronDown,
  faEye,
  faFileCirclePlus,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { updateAppAlert } from '@labrav/common'
import {
  FundingBenefitsAccessReason,
  getFundingBenefitsAccess,
} from '../../api/fundingApi'
import {
  FundingBenefitTypeItem,
  FundingSettingsAuditEntry,
  FundingSettings as FundingSettingsData,
  FundingSettingsAutomation,
  getFundingDefaultFieldValues,
  getFundingSettings,
  getFundingBenefitTypes,
  patchFundingSettings,
  putFundingDefaultFieldValues,
} from '../../api/fundingSettingsApi'
import { FundingSetupContext } from '../../types'
import {
  FundingBenefitsSetupPage,
  FundingIamDeniedCta,
} from '../FundingBenefitsSetupPage/FundingBenefitsSetupPage'
import {
  FundingTemplateDetail,
  FundingTemplateFormField,
  FundingTemplateListItem,
  getFundingTemplate,
  listFundingTemplates,
} from '../../api/fundingTemplatesApi'
import { formatActivityTime, formatWalletDate } from '../../utils/format'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { AutoSubmitConfirmDialog } from './AutoSubmitConfirmDialog'
import { AUDIT_WATERMARK_SVG } from './auditWatermark'
import {
  SearchableCheckboxOption,
  SearchableCheckboxSelect,
} from './SearchableCheckboxSelect'
import { TemplateDetailPage } from './TemplateDetailPage'
import { useStyles } from './FundingSettings.styles'
import {
  buildProgramRows,
  canToggleAutoSubmit,
  displayFundingProgramLabel,
  isBrowsableFundingType,
  isMpoppType,
  isSettingsCoreProgram,
  relatedBenefitIdsForSettings,
  supportsAutoDraftAi,
  supportsFundingTemplates,
  synthesizeAutomation,
} from './fundingProgramRows'

const ALL_APPROVED_US_STATES = 'All Approved US States'
const CRITERIA_PREVIEW_CHARS = 90

const automationKey = (a: FundingSettingsAutomation) =>
  `${a.program}:${a.program_label}`

const toCheckboxOptions = (
  field: FundingTemplateFormField | undefined
): SearchableCheckboxOption[] => {
  const values = field?.enum ?? []
  const labels = field?.option_labels ?? {}
  // Display names must come from API `option_labels`.
  return values.map(value => ({
    value,
    label: labels[value] ?? value,
  }))
}

const resolveCountryCode = (
  raw: string,
  options: SearchableCheckboxOption[]
) => {
  if (!raw) return ''
  return options.some(o => o.value === raw) ? raw : ''
}

const parseStates = (raw: string | null | undefined) => {
  if (!raw?.trim()) return [] as string[]
  const text = raw.trim()
  if (text === ALL_APPROVED_US_STATES) return [ALL_APPROVED_US_STATES]
  return text
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
}

const serializeStates = (values: string[]) => {
  if (values.includes(ALL_APPROVED_US_STATES)) return ALL_APPROVED_US_STATES
  return values.join(',')
}

/** Figma Basic information labels → schema paths for global defaults. */
const DEFAULT_PATHS = {
  jobTitle: 'PartnerContacts.0.BusinessTitle',
  firstName: 'PartnerContacts.0.FirstName',
  lastName: 'PartnerContacts.0.LastName',
  email: 'PartnerContacts.0.Email',
  executedCountry: 'ExecutedCountry',
  executedState: 'ExecutedState',
  awsAccountId: 'AwsAccountId',
  /** Partner-level optional flag (not an AWS Partner Central schema path). */
  hasScaWithAws: 'HasScaWithAws',
} as const

const SCA_WHY_NEEDED =
  'Strategic Collaboration Agreements (SCA) with AWS affect which funding programs and budget pools you can use. This helps Labra route requests correctly.'

const AUTO_SUBMIT_COLUMN_TOOLTIP =
  'The Labra AI agent auto-drafts the AWS funding request, pre-filling every field it can from available data and flagging any that need input. Your team member reviews and edits the draft, then approves it for submission to AWS. Nothing is submitted without human approval.'

const NOT_APPLICABLE_TOOLTIP = 'Not applicable for this program'

const InfoIcon = ({
  className,
  tooltip,
}: {
  className: string
  tooltip?: ReactNode
}) => {
  const icon = <FontAwesomeIcon icon={faCircleInfo} className={className} />
  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <span>{icon}</span>
    </Tooltip>
  ) : (
    icon
  )
}

const BasicFloatInput = ({
  label,
  value,
  onChange,
  maxLength,
  inputMode,
  error,
  infoTooltip,
  digitsOnly,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  maxLength?: number
  inputMode?: 'numeric' | 'email' | 'text'
  error?: boolean
  infoTooltip?: ReactNode
  digitsOnly?: boolean
}) => {
  const { classes, cx } = useStyles()
  const [focused, setFocused] = useState(false)
  const floated = focused || Boolean(value)
  return (
    <div
      className={cx(
        classes.floatField,
        floated && classes.floatFieldFloated,
        error && classes.floatFieldError
      )}
    >
      <Typography
        component="span"
        className={cx(
          classes.floatLabelInField,
          floated && classes.floatLabelFloated
        )}
      >
        {label}
      </Typography>
      <input
        className={classes.input}
        aria-label={label}
        value={value}
        maxLength={maxLength}
        inputMode={inputMode}
        pattern={digitsOnly ? '[0-9]*' : undefined}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={e => {
          if (!digitsOnly) return
          if (
            e.ctrlKey ||
            e.metaKey ||
            e.altKey ||
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Enter' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Home' ||
            e.key === 'End'
          ) {
            return
          }
          if (!/^\d$/.test(e.key)) e.preventDefault()
        }}
        onPaste={e => {
          if (!digitsOnly) return
          e.preventDefault()
          const pasted = e.clipboardData.getData('text') || ''
          let next = `${value}${pasted.replace(/\D/g, '')}`
          if (maxLength != null) next = next.slice(0, maxLength)
          onChange(next)
        }}
        onChange={e => {
          let next = e.target.value
          if (digitsOnly) next = next.replace(/\D/g, '')
          if (maxLength != null) next = next.slice(0, maxLength)
          onChange(next)
        }}
      />
      {infoTooltip ? (
        <InfoIcon className={classes.inputInfoIcon} tooltip={infoTooltip} />
      ) : null}
    </div>
  )
}

interface FundingSettingsProps {
  serviceUrl?: string
  partnerId?: string
  updaterName?: string
  updaterLabraUserId?: string
  /** Template detail breadcrumbs replace the settings page header title. */
  onHeaderChange?: (header: ReactNode | null) => void
  /** Prefills for Benefits API PDM setup email. */
  setupContext?: FundingSetupContext
}

const confirmCopy = (programLabel: string, enabling: boolean) =>
  enabling
    ? {
        title: `Enable auto-submit for ${programLabel}?`,
        bodyLines: [
          `Are you sure you want to enable auto-submit for ${programLabel}? Your eligible funding requests will be submitted automatically.`,
        ],
      }
    : {
        title: `Disable auto-submit for ${programLabel}?`,
        bodyLines: [
          `Are you sure you want to disable auto-submit for ${programLabel}? You will have to manually submit your funding requests for this funding program.`,
        ],
      }

const PAYEE_ID_LENGTH = 13
const PAYEE_ID_ERROR = `Payee ID must be ${PAYEE_ID_LENGTH} characters`
const AWS_ACCOUNT_HELPER =
  "AWS account to receive approved credits or cash. They can't be transferred or reversed."
const PAYEE_DOCS_URL =
  'https://helpcenter.labra.io/hc/en-us/articles/54258496339475-How-to-find-your-AWS-Payee-ID'

const splitFullName = (fullName: string) => {
  const trimmed = fullName.trim()
  if (!trimmed) return { first: '', last: '' }
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

const joinFullName = (first?: string | null, last?: string | null) =>
  [first, last].filter(Boolean).join(' ').trim()

export const FundingSettings = ({
  serviceUrl,
  partnerId,
  updaterName,
  updaterLabraUserId,
  onHeaderChange,
  setupContext,
}: FundingSettingsProps) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [benefitsAccessReason, setBenefitsAccessReason] = useState<
    FundingBenefitsAccessReason | null
  >(null)
  const [benefitsAccessReady, setBenefitsAccessReady] = useState(false)
  const [mpoppGrowWalletFound, setMpoppGrowWalletFound] = useState(false)

  const [expandedProgramKey, setExpandedProgramKey] = useState<string | null>(
    null
  )
  const [pendingExpandBenefitId, setPendingExpandBenefitId] = useState<
    string | null
  >(null)
  const [criteriaExpandedKey, setCriteriaExpandedKey] = useState<string | null>(
    null
  )
  const [archivedExpanded, setArchivedExpanded] = useState(false)
  const [view, setView] = useState<'settings' | 'template-detail'>('settings')
  const [detailMode, setDetailMode] = useState<'create' | 'edit'>('create')
  const [editingTemplate, setEditingTemplate] =
    useState<FundingTemplateDetail | null>(null)
  const [createBenefitId, setCreateBenefitId] = useState<string | undefined>()
  const [detailProgramLabel, setDetailProgramLabel] = useState('')
  const [globalsConfigured, setGlobalsConfigured] = useState(false)
  const [browseAnchorEl, setBrowseAnchorEl] = useState<HTMLElement | null>(
    null
  )
  const [browseSearch, setBrowseSearch] = useState('')
  const [browseCatalogTypes, setBrowseCatalogTypes] = useState<
    FundingBenefitTypeItem[]
  >([])
  const [browseTypesLoading, setBrowseTypesLoading] = useState(false)
  const [browseTypesError, setBrowseTypesError] = useState(false)
  /** Programs pinned from Browse before any template exists. */
  const [pinnedBenefitIds, setPinnedBenefitIds] = useState<string[]>([])
  const [settingsLoading, setSettingsLoading] = useState(true)

  const [basicOpen, setBasicOpen] = useState(true)
  const [programsOpen, setProgramsOpen] = useState(true)

  const [payeeId, setPayeeId] = useState('')
  const [savedPayeeId, setSavedPayeeId] = useState('')
  const [payeeError, setPayeeError] = useState(false)
  const [awsAccountId, setAwsAccountId] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [executedCountry, setExecutedCountry] = useState('')
  const [executedStates, setExecutedStates] = useState<string[]>([])
  const [hasScaWithAws, setHasScaWithAws] = useState('')
  const [countryOptions, setCountryOptions] = useState<
    SearchableCheckboxOption[]
  >([])
  const [stateOptions, setStateOptions] = useState<SearchableCheckboxOption[]>(
    []
  )
  const [defaultsSnapshot, setDefaultsSnapshot] = useState<
    Record<string, string | null>
  >({})

  const [automation, setAutomation] = useState<FundingSettingsAutomation[]>([])
  const automationRef = useRef<FundingSettingsAutomation[]>([])
  automationRef.current = automation
  const settingsPatchGen = useRef(0)
  const [eligibleTypes, setEligibleTypes] = useState<FundingBenefitTypeItem[]>(
    []
  )
  const [templates, setTemplates] = useState<FundingTemplateListItem[]>([])
  const [auditLog, setAuditLog] = useState<FundingSettingsAuditEntry[]>([])
  const auditRef = useRef<HTMLDivElement>(null)
  const [pending, setPending] = useState<{
    key: string
    next: boolean
    automation: FundingSettingsAutomation
  } | null>(null)

  const alertError = useCallback(
    (message: string) => {
      dispatch(
        updateAppAlert({ message, messageType: 'ERROR', autoClose: true })
      )
    },
    [dispatch]
  )

  const alertSuccess = useCallback(
    (message: string) => {
      dispatch(
        updateAppAlert({
          message,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    },
    [dispatch]
  )

  const applyDefaults = (
    values: Record<string, string | null> | null,
    countries: SearchableCheckboxOption[] = countryOptions
  ) => {
    const v = values ?? {}
    setDefaultsSnapshot(v)
    setAwsAccountId((v[DEFAULT_PATHS.awsAccountId] ?? '').slice(0, 12))
    setJobTitle(v[DEFAULT_PATHS.jobTitle] ?? '')
    setFullName(
      joinFullName(v[DEFAULT_PATHS.firstName], v[DEFAULT_PATHS.lastName])
    )
    setEmail(v[DEFAULT_PATHS.email] ?? '')
    const country = resolveCountryCode(
      v[DEFAULT_PATHS.executedCountry] ?? '',
      countries
    )
    setExecutedCountry(country)
    setExecutedStates(
      country === 'US' ? parseStates(v[DEFAULT_PATHS.executedState]) : []
    )
    const scaRaw = (v[DEFAULT_PATHS.hasScaWithAws] ?? '').trim().toLowerCase()
    setHasScaWithAws(
      scaRaw === 'yes' || scaRaw === 'true'
        ? 'yes'
        : scaRaw === 'no' || scaRaw === 'false'
          ? 'no'
          : ''
    )
  }

  const applyEnumFields = (
    fields: FundingTemplateFormField[] | null | undefined
  ) => {
    const byPath = new Map((fields ?? []).map(f => [f.path, f] as const))
    const countries = toCheckboxOptions(byPath.get(DEFAULT_PATHS.executedCountry))
    const states = toCheckboxOptions(byPath.get(DEFAULT_PATHS.executedState))
    setCountryOptions(countries)
    setStateOptions(states)
    return { countries, states }
  }

  const reload = useCallback(async () => {
    if (!serviceUrl || !partnerId) return
    try {
      const [settingsRes, defaultsRes, typesRes, templatesRes] =
        await Promise.all([
          getFundingSettings(serviceUrl, partnerId),
          getFundingDefaultFieldValues(serviceUrl, partnerId),
          getFundingBenefitTypes(serviceUrl),
          listFundingTemplates(serviceUrl, partnerId, {
            include_archived: true,
          }),
        ])
      const data = settingsRes.data
      setPayeeId(data.payee_id ?? '')
      setSavedPayeeId(data.payee_id ?? '')
      const nextAutomation = data.automation ?? []
      setAutomation(nextAutomation)
      automationRef.current = nextAutomation
      setAuditLog(data.audit_log ?? [])
      const { countries } = applyEnumFields(defaultsRes.data.fields)
      applyDefaults(defaultsRes.data.values, countries)
      setGlobalsConfigured(Boolean(defaultsRes.data.configured))
      setEligibleTypes(typesRes.data.types ?? [])
      setTemplates(templatesRes.data.templates ?? [])
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string; message?: string } }
        message?: string
      }
      alertError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load funding settings. Please try again.'
      )
    } finally {
      setSettingsLoading(false)
    }
  }, [serviceUrl, partnerId, alertError])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!serviceUrl || !partnerId) return
    let ignore = false
    setBenefitsAccessReady(false)

    const apply = (
      reason: FundingBenefitsAccessReason,
      walletFound: boolean
    ) => {
      setBenefitsAccessReason(reason)
      setMpoppGrowWalletFound(walletFound)
      setBenefitsAccessReady(true)
    }

    const probe = async (refresh: boolean) => {
      try {
        const { data } = await getFundingBenefitsAccess(
          serviceUrl,
          partnerId,
          refresh ? { refresh: true } : undefined
        )
        if (ignore) return
        apply(data.reason ?? 'error', Boolean(data.mpopp_grow_wallet_found))
      } catch {
        if (!ignore) {
          // Fail open so Settings remain usable if the probe is unavailable.
          apply('ok', false)
        }
      }
    }

    void probe(false)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void probe(true)
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      ignore = true
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [serviceUrl, partnerId])

  const save = (
    body: Partial<FundingSettingsData>,
    { rollback, onSaved }: { rollback?: () => void; onSaved?: () => void } = {}
  ) => {
    if (!serviceUrl || !partnerId) return
    const gen = ++settingsPatchGen.current
    const actor = updaterName || updaterLabraUserId
    const patch = {
      ...body,
      ...(actor ? { updated_by: actor } : {}),
      ...(updaterLabraUserId
        ? { updated_by_labra_user_id: updaterLabraUserId }
        : {}),
    }
    patchFundingSettings(serviceUrl, partnerId, patch)
      .then(({ data }) => {
        if (gen !== settingsPatchGen.current) return
        if (data?.audit_log) setAuditLog(data.audit_log)
        if (onSaved) {
          onSaved()
          return
        }
        alertSuccess('Funding settings updated successfully.')
      })
      .catch(error => {
        if (gen !== settingsPatchGen.current) return
        rollback?.()
        alertError(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error?.message ||
            'Failed to update funding settings.'
        )
      })
  }

  const resetBasic = () => {
    setPayeeId('')
    setPayeeError(false)
    setAwsAccountId('')
    setJobTitle('')
    setFullName('')
    setEmail('')
    setExecutedCountry('')
    setExecutedStates([])
    setHasScaWithAws('')
  }

  const saveBasic = async () => {
    if (!serviceUrl || !partnerId) return
    const payee = payeeId.trim()
    if (payee && payee.length !== PAYEE_ID_LENGTH) {
      setPayeeError(true)
      return
    }
    setPayeeError(false)
    const { first, last } = splitFullName(fullName)
    const country = executedCountry.trim()
    const values: Record<string, string | null> = {
      [DEFAULT_PATHS.jobTitle]: jobTitle.trim() || null,
      [DEFAULT_PATHS.firstName]: first || null,
      [DEFAULT_PATHS.lastName]: last || null,
      [DEFAULT_PATHS.email]: email.trim() || null,
      [DEFAULT_PATHS.executedCountry]: country || null,
      // States only apply for United States (US).
      [DEFAULT_PATHS.executedState]:
        country === 'US'
          ? serializeStates(executedStates).trim() || null
          : null,
      [DEFAULT_PATHS.awsAccountId]: awsAccountId.trim() || null,
      [DEFAULT_PATHS.hasScaWithAws]: hasScaWithAws.trim() || null,
    }
    const actor = updaterName || updaterLabraUserId
    const previousSavedPayee = savedPayeeId
    let payeePatched = false
    try {
      if (payee && payee !== savedPayeeId) {
        const { data } = await patchFundingSettings(serviceUrl, partnerId, {
          payee_id: payee,
          ...(actor ? { updated_by: actor } : {}),
          ...(updaterLabraUserId
            ? { updated_by_labra_user_id: updaterLabraUserId }
            : {}),
        })
        payeePatched = true
        if (data?.audit_log) setAuditLog(data.audit_log)
      }
      const { data } = await putFundingDefaultFieldValues(
        serviceUrl,
        partnerId,
        values,
        {
          ...(actor ? { updatedBy: actor } : {}),
          ...(updaterLabraUserId
            ? { updatedByLabraUserId: updaterLabraUserId }
            : {}),
        }
      )
      if (payeePatched) setSavedPayeeId(payee)
      applyDefaults(data.values, countryOptions)
      setGlobalsConfigured(
        data.configured != null ? Boolean(data.configured) : true
      )
      if (data?.audit_log) setAuditLog(data.audit_log)
      alertSuccess('Basic information saved.')
    } catch (error: unknown) {
      if (payeePatched) {
        // Keep UI dirty for payee until both steps succeed; server may already
        // have the new payee — reload on next visit reconciles.
        setSavedPayeeId(previousSavedPayee)
      }
      const err = error as {
        response?: { data?: { detail?: string; message?: string } }
        message?: string
      }
      alertError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to save basic information.'
      )
    }
  }

  const setAutoSubmit = (
    key: string,
    value: boolean,
    seed?: FundingSettingsAutomation
  ) => {
    const previous = automationRef.current
    const hasKey = previous.some(a => automationKey(a) === key)
    if (!hasKey && !seed) {
      alertError('Cannot update auto-submit: missing program enrollment.')
      return
    }
    const next = hasKey
      ? previous.map(a =>
          automationKey(a) === key ? { ...a, auto_submit: value } : a
        )
      : [
          ...previous,
          {
            ...seed!,
            auto_submit: value,
          },
        ]
    setAutomation(next)
    automationRef.current = next
    save(
      { automation: next },
      {
        rollback: () => {
          setAutomation(previous)
          automationRef.current = previous
        },
      }
    )
  }

  const pendingProgram = pending?.automation ?? null
  const copy =
    pendingProgram && pending
      ? confirmCopy(pendingProgram.program_label, pending.next)
      : { title: '', bodyLines: [] }

  const benefitIdsWithTemplates = useMemo(() => {
    const ids = new Set<string>()
    for (const t of templates) {
      const id = t.sub_funding_type.benefit_identifier
      if (id) ids.add(id)
    }
    return ids
  }, [templates])

  const templateCountForBenefit = (benefitId: string) => {
    const related = relatedBenefitIdsForSettings(benefitId)
    let count = 0
    for (const t of templates) {
      if (t.status === 'archived') continue
      const id = t.sub_funding_type.benefit_identifier
      if (id && related.has(id)) count += 1
    }
    return count
  }

  const programRows = buildProgramRows(
    automation,
    eligibleTypes,
    benefitIdsWithTemplates,
    pinnedBenefitIds
  )

  /** Non-deployed catalog rows for Browse; core seven (not yet on matrix) first. */
  const browseSubPrograms = useMemo(() => {
    const deployedIds = new Set(
      programRows
        .map(row => row.eligible?.id)
        .filter((id): id is string => Boolean(id))
    )
    return browseCatalogTypes
      .filter(t => isBrowsableFundingType(t) && !deployedIds.has(t.id))
      .slice()
      .sort((a, b) => {
        const aCore = isSettingsCoreProgram(a) ? 0 : 1
        const bCore = isSettingsCoreProgram(b) ? 0 : 1
        if (aCore !== bCore) return aCore - bCore
        return displayFundingProgramLabel(a.program).localeCompare(
          displayFundingProgramLabel(b.program)
        )
      })
  }, [browseCatalogTypes, programRows])

  const browseFilteredPrograms = useMemo(() => {
    const query = browseSearch.trim().toLowerCase()
    if (!query) return browseSubPrograms
    return browseSubPrograms.filter(type =>
      displayFundingProgramLabel(type.program)
        .toLowerCase()
        .includes(query)
    )
  }, [browseSubPrograms, browseSearch])

  const closeBrowseMenu = () => {
    setBrowseAnchorEl(null)
    setBrowseSearch('')
    setBrowseTypesError(false)
  }

  const openBrowseMenu = async (anchor: HTMLElement) => {
    setBrowseAnchorEl(anchor)
    setBrowseSearch('')
    setBrowseTypesError(false)
    if (!serviceUrl) return
    setBrowseTypesLoading(true)
    try {
      const { data } = await getFundingBenefitTypes(serviceUrl)
      setBrowseCatalogTypes(data.types ?? [])
    } catch {
      setBrowseCatalogTypes([])
      setBrowseTypesError(true)
    } finally {
      setBrowseTypesLoading(false)
    }
  }

  const templatesForBenefit = (benefitId: string) => {
    const related = relatedBenefitIdsForSettings(benefitId)
    return templates.filter(t => {
      const id = t.sub_funding_type.benefit_identifier
      return Boolean(id && related.has(id))
    })
  }

  const closeTemplateDetail = () => {
    setView('settings')
    setEditingTemplate(null)
    setCreateBenefitId(undefined)
    setDetailProgramLabel('')
  }

  const openCreateTemplate = (benefitId?: string, programLabel?: string) => {
    if (!benefitId) return
    closeBrowseMenu()
    setDetailMode('create')
    setEditingTemplate(null)
    setCreateBenefitId(benefitId)
    setDetailProgramLabel(programLabel ?? '')
    setView('template-detail')
  }

  /** Browse: pin program on the matrix, expand it, then add templates from the row. */
  const addProgramFromBrowse = (type: FundingBenefitTypeItem) => {
    closeBrowseMenu()
    setEligibleTypes(prev =>
      prev.some(t => t.id === type.id) ? prev : [...prev, type]
    )
    setPinnedBenefitIds(prev =>
      prev.includes(type.id) ? prev : [...prev, type.id]
    )
    if (supportsFundingTemplates(type)) {
      setPendingExpandBenefitId(type.id)
    }
  }

  useEffect(() => {
    if (view !== 'settings' || !pendingExpandBenefitId) return
    const related = relatedBenefitIdsForSettings(pendingExpandBenefitId)
    const row = programRows.find(
      r => r.eligible?.id && related.has(r.eligible.id)
    )
    if (!row?.eligible) return
    if (!supportsFundingTemplates(row.eligible)) {
      setPendingExpandBenefitId(null)
      return
    }
    setExpandedProgramKey(row.key)
    setPendingExpandBenefitId(null)
  }, [view, pendingExpandBenefitId, programRows])

  const openEditTemplate = (
    templateId: string,
    programLabel?: string
  ) => {
    if (!serviceUrl || !partnerId) return
    const listItem = templates.find(t => t.template_id === templateId)
    const applyDetail = (data: FundingTemplateDetail) => {
      setDetailMode('edit')
      setEditingTemplate(data)
      setCreateBenefitId(undefined)
      setDetailProgramLabel(
        programLabel ?? data.sub_funding_type.program_name ?? ''
      )
      setView('template-detail')
    }
    // Navigate immediately with list stub so summary/AI paint while detail +
    // form-schema + chips fetch in parallel.
    if (listItem) {
      applyDetail({
        template_id: listItem.template_id,
        name: listItem.name,
        description: listItem.description,
        sub_funding_type: listItem.sub_funding_type,
        status: listItem.status,
        force_aws_account_id: false,
        resources: null,
        resources_summary: listItem.resources_summary,
        resources_configured_count: listItem.resources_configured_count,
        defaults_mode: 'inherit_globals',
        global_defaults: null,
        field_overrides: null,
        resolved_fields: {},
        created_by: listItem.created_by,
        updated_by: listItem.updated_by,
        created_at: listItem.created_at,
        updated_at: listItem.updated_at ?? listItem.created_at,
        funding_request_count: listItem.funding_request_count,
      })
    }
    void getFundingTemplate(serviceUrl, partnerId, templateId)
      .then(({ data }) => applyDetail(data))
      .catch(() => {
        alertError('Failed to load template.')
        if (!listItem) return
        setView('settings')
        setEditingTemplate(null)
      })
  }

  const isUsExecutedCountry = executedCountry === 'US'
  const awsAccountTrimmed = awsAccountId.trim()
  const awsAccountInvalid = Boolean(
    awsAccountTrimmed && !/^\d{12}$/.test(awsAccountTrimmed)
  )
  const awsAccountErrorMessage = !awsAccountTrimmed
    ? ''
    : !/^\d+$/.test(awsAccountTrimmed)
      ? 'AWS account ID must contain only numbers.'
      : awsAccountTrimmed.length !== 12
        ? 'AWS account ID must be exactly 12 digits.'
        : ''
  const basicIncomplete =
    !payeeId.trim() ||
    !awsAccountTrimmed ||
    awsAccountInvalid ||
    !jobTitle.trim() ||
    !fullName.trim() ||
    !email.trim() ||
    !executedCountry.trim() ||
    (isUsExecutedCountry && executedStates.length === 0)

  const basicDirty = useMemo(() => {
    const norm = (value: string | null | undefined) => (value ?? '').trim()
    if (norm(payeeId) !== norm(savedPayeeId)) return true
    if (norm(awsAccountId) !== norm(defaultsSnapshot[DEFAULT_PATHS.awsAccountId]))
      return true
    if (norm(jobTitle) !== norm(defaultsSnapshot[DEFAULT_PATHS.jobTitle]))
      return true
    if (
      norm(fullName) !==
      joinFullName(
        defaultsSnapshot[DEFAULT_PATHS.firstName],
        defaultsSnapshot[DEFAULT_PATHS.lastName]
      )
    ) {
      return true
    }
    if (norm(email) !== norm(defaultsSnapshot[DEFAULT_PATHS.email])) return true
    const snapCountry = resolveCountryCode(
      defaultsSnapshot[DEFAULT_PATHS.executedCountry] ?? '',
      countryOptions
    )
    if (executedCountry !== snapCountry) return true
    const snapStates =
      snapCountry === 'US'
        ? serializeStates(
            parseStates(defaultsSnapshot[DEFAULT_PATHS.executedState])
          )
        : ''
    const currentStates =
      executedCountry === 'US' ? serializeStates(executedStates) : ''
    if (currentStates !== snapStates) return true
    const snapSca = (defaultsSnapshot[DEFAULT_PATHS.hasScaWithAws] ?? '')
      .trim()
      .toLowerCase()
    const snapScaNorm =
      snapSca === 'yes' || snapSca === 'true'
        ? 'yes'
        : snapSca === 'no' || snapSca === 'false'
          ? 'no'
          : ''
    return hasScaWithAws !== snapScaNorm
  }, [
    payeeId,
    savedPayeeId,
    awsAccountId,
    jobTitle,
    fullName,
    email,
    executedCountry,
    executedStates,
    hasScaWithAws,
    defaultsSnapshot,
    countryOptions,
  ])

  const basicSaveDisabled = !basicDirty || basicIncomplete
  const basicSaveDisabledReason = basicIncomplete
    ? 'Please fill the above input fields to enable this button.'
    : !basicDirty
      ? 'Update the above input fields to enable this button.'
      : ''

  const programsIncomplete = eligibleTypes.some(
    t =>
      isSettingsCoreProgram(t) &&
      !isMpoppType(t) &&
      templateCountForBenefit(t.id) === 0
  )

  if (!benefitsAccessReady) {
    return null
  }

  if (benefitsAccessReason === 'feature_not_enabled') {
    return (
      <FundingBenefitsSetupPage
        mpoppGrowWalletFound={mpoppGrowWalletFound}
        setupContext={setupContext}
      />
    )
  }

  if (
    benefitsAccessReason === 'iam_denied' ||
    benefitsAccessReason === 'role_missing'
  ) {
    return <FundingIamDeniedCta />
  }

  if (
    view === 'template-detail' &&
    serviceUrl &&
    partnerId &&
    (detailMode === 'edit' ? editingTemplate : createBenefitId)
  ) {
    const detailBenefitId =
      detailMode === 'edit' && editingTemplate
        ? editingTemplate.sub_funding_type.benefit_identifier
        : (createBenefitId as string)
    const detailProgramRow = programRows.find(
      row =>
        row.eligible?.id === detailBenefitId ||
        row.label === detailProgramLabel
    )
    return (
      <TemplateDetailPage
        mode={detailMode}
        serviceUrl={serviceUrl}
        partnerId={partnerId}
        programLabel={detailProgramLabel}
        benefitIdentifier={detailBenefitId}
        template={detailMode === 'edit' ? editingTemplate : null}
        programDescription={detailProgramRow?.description ?? null}
        eligibilityCriteria={
          detailProgramRow?.eligibilityCriteria ??
          detailProgramRow?.eligible?.eligibility_criteria ??
          null
        }
        autoSubmitLimitUsd={
          detailProgramRow?.automation?.auto_submission_limit_usd ?? null
        }
        globalDefaults={defaultsSnapshot}
        globalsConfigured={globalsConfigured}
        updaterName={updaterName}
        updaterLabraUserId={updaterLabraUserId}
        onHeaderChange={onHeaderChange}
        onDiscard={() => {
          closeTemplateDetail()
          void reload()
        }}
        onSaved={(detail, meta) => {
          const kind = meta?.kind
          if (kind === 'autosaved') {
            alertSuccess('Saved successfully')
          } else {
            alertSuccess(
              kind === 'archived'
                ? 'Template archived.'
                : kind === 'unarchived'
                  ? 'Template unarchived.'
                  : detailMode === 'create'
                    ? 'Template created.'
                    : 'Saved successfully'
            )
          }
          setDetailMode('edit')
          setEditingTemplate(detail)
          setCreateBenefitId(undefined)
          setPendingExpandBenefitId(
            detail.sub_funding_type.benefit_identifier ?? null
          )
          void reload()
        }}
        onCloned={detail => {
          alertSuccess('Template cloned.')
          setDetailMode('edit')
          setEditingTemplate(detail)
          setCreateBenefitId(undefined)
          setDetailProgramLabel(detail.sub_funding_type.program_name ?? '')
          setPendingExpandBenefitId(
            detail.sub_funding_type.benefit_identifier ?? null
          )
          void reload()
        }}
        onError={alertError}
      />
    )
  }

  return (
    <div className={classes.root} data-testid="funding-settings">
      <div className={classes.leftColumn}>
        <section className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerTitleGroup}>
                <span
                  className={cx(
                    classes.iconBox,
                    basicIncomplete
                      ? classes.statusIconError
                      : classes.statusIconOk
                  )}
                >
                  <FontAwesomeIcon
                    icon={
                      basicIncomplete ? faCircleExclamation : faCircleCheck
                    }
                  />
                </span>
                <Typography component="h2" className={classes.cardTitle}>
                  Basic information
                </Typography>
              </div>
            </div>
            <button
              type="button"
              className={classes.collapseButton}
              aria-label={basicOpen ? 'Collapse' : 'Expand'}
              aria-expanded={basicOpen}
              onClick={() => setBasicOpen(v => !v)}
            >
              <FontAwesomeIcon icon={basicOpen ? faChevronUp : faChevronDown} />
            </button>
          </div>

          {basicOpen && settingsLoading && (
            <div
              className={classes.basicGrid}
              role="status"
              aria-label="Loading basic information"
              data-testid="basic-information-skeleton"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={classes.basicField}>
                  <span
                    className={cx(classes.skeletonBar, classes.skeletonField)}
                  />
                  {i < 2 ? (
                    <span
                      className={cx(
                        classes.skeletonBar,
                        classes.skeletonHelper
                      )}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {basicOpen && !settingsLoading && (
            <>
              <div className={classes.basicGrid}>
                <div className={classes.basicField}>
                  <BasicFloatInput
                    label="Amazon Central Payee Account ID"
                    value={payeeId}
                    maxLength={PAYEE_ID_LENGTH}
                    error={payeeError}
                    onChange={next => {
                      setPayeeId(next)
                      setPayeeError(false)
                    }}
                    infoTooltip={
                      <>
                        AWS Payee ID is your company&apos;s identifier in
                        Amazon Payee Central, used for cash payouts/invoices
                        from AWS/Amazon funding programs.
                      </>
                    }
                  />
                  <a
                    href={PAYEE_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.howToFindLink}
                  >
                    How to find it?
                  </a>
                  {payeeError && (
                    <Typography
                      component="span"
                      className={classes.helperTextError}
                    >
                      {PAYEE_ID_ERROR}
                    </Typography>
                  )}
                </div>

                <div className={classes.basicField}>
                  <BasicFloatInput
                    label="AWS account ID"
                    value={awsAccountId}
                    maxLength={12}
                    error={awsAccountInvalid}
                    onChange={next =>
                      setAwsAccountId(next.replace(/[\r\n]/g, '').slice(0, 12))
                    }
                    infoTooltip={AWS_ACCOUNT_HELPER}
                  />
                  <Typography
                    component="span"
                    className={
                      awsAccountErrorMessage
                        ? classes.helperTextError
                        : classes.helperText
                    }
                  >
                    {awsAccountErrorMessage || AWS_ACCOUNT_HELPER}
                  </Typography>
                </div>

                <div className={classes.basicField}>
                  <BasicFloatInput
                    label="Job title"
                    value={jobTitle}
                    onChange={setJobTitle}
                  />
                </div>

                <div className={classes.basicField}>
                  <BasicFloatInput
                    label="Full name"
                    value={fullName}
                    onChange={setFullName}
                  />
                </div>

                <div className={classes.basicField}>
                  <BasicFloatInput
                    label="Email"
                    value={email}
                    inputMode="email"
                    onChange={setEmail}
                  />
                </div>
                <div className={classes.basicField}>
                  <SearchableCheckboxSelect
                    id="executed-country"
                    label="Country"
                    floatingLabel
                    options={countryOptions}
                    value={executedCountry ? [executedCountry] : []}
                    maxSelections={1}
                    placeholder=""
                    onChange={next => {
                      const nextCountry = next[0] ?? ''
                      setExecutedCountry(nextCountry)
                      if (nextCountry !== 'US') {
                        setExecutedStates([])
                      }
                    }}
                  />
                </div>
                <div className={classes.basicField}>
                  <div
                    className={cx(
                      classes.floatField,
                      classes.floatFieldFloated,
                      classes.scaFloatField
                    )}
                    data-testid="has-sca-with-aws"
                  >
                    <Typography
                      component="span"
                      className={cx(
                        classes.floatLabelInField,
                        classes.floatLabelFloated
                      )}
                    >
                      Do you have an SCA with AWS? (Optional)
                    </Typography>
                    <div
                      className={classes.scaRadioRow}
                      role="radiogroup"
                      aria-label="Do you have an SCA with AWS? (Optional)"
                    >
                      <label className={classes.scaRadioOption}>
                        <input
                          type="radio"
                          name="has-sca-with-aws"
                          aria-label="SCA with AWS Yes"
                          checked={hasScaWithAws === 'yes'}
                          onChange={() => setHasScaWithAws('yes')}
                        />
                        Yes
                      </label>
                      <label className={classes.scaRadioOption}>
                        <input
                          type="radio"
                          name="has-sca-with-aws"
                          aria-label="SCA with AWS No"
                          checked={hasScaWithAws === 'no'}
                          onChange={() => setHasScaWithAws('no')}
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <Tooltip title={SCA_WHY_NEEDED} arrow placement="top">
                    <button
                      type="button"
                      className={classes.howToFindLink}
                      data-testid="sca-why-needed"
                    >
                      Why this info is needed?
                    </button>
                  </Tooltip>
                </div>
                <div className={classes.basicField}>
                  {isUsExecutedCountry ? (
                    <SearchableCheckboxSelect
                      id="executed-state"
                      label="State"
                      floatingLabel
                      options={stateOptions}
                      value={executedStates}
                      placeholder=""
                      onChange={next => {
                        if (next.includes(ALL_APPROVED_US_STATES)) {
                          setExecutedStates([ALL_APPROVED_US_STATES])
                          return
                        }
                        setExecutedStates(
                          next.filter(v => v !== ALL_APPROVED_US_STATES)
                        )
                      }}
                    />
                  ) : null}
                </div>
              </div>

              <div className={classes.basicActions}>
                <button
                  type="button"
                  className={classes.resetButton}
                  onClick={resetBasic}
                >
                  Reset
                </button>
                <Tooltip
                  title={basicSaveDisabledReason}
                  arrow
                  placement="top"
                  disableHoverListener={!basicSaveDisabled}
                >
                  <span>
                    <button
                      type="button"
                      className={classes.saveOutlined}
                      onClick={() => void saveBasic()}
                      disabled={basicSaveDisabled}
                      data-testid="save-basic-information"
                    >
                      Save
                    </button>
                  </span>
                </Tooltip>
              </div>
            </>
          )}
        </section>

        <section className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerTitleGroup}>
                <span
                  className={cx(classes.iconBox, classes.statusIconMuted)}
                >
                  <FontAwesomeIcon icon={faCircleCheck} />
                </span>
                <Typography component="h2" className={classes.cardTitle}>
                  Funding request templates and auto-submissions
                </Typography>
              </div>
            </div>
            <button
              type="button"
              className={classes.collapseButton}
              aria-label={programsOpen ? 'Collapse' : 'Expand'}
              aria-expanded={programsOpen}
              onClick={() => setProgramsOpen(v => !v)}
            >
              <FontAwesomeIcon
                icon={programsOpen ? faChevronUp : faChevronDown}
              />
            </button>
          </div>

          {programsOpen && (
            <>
              <Typography component="p" className={classes.programsCopy}>
                Save time on repeat funding requests with templates. Create a
                template once to pre-fill common fields, then apply it to any
                submission, whether you submit manually or use AI agent assisted
                auto-submission.
              </Typography>

              <div className={classes.programsTable}>
                <div className={classes.programsHead}>
                  <span>Funding program</span>
                  <span className={classes.programsHeadCount}>
                    Number of template(s)
                  </span>
                  <span className={classes.programsHeadAutoSubmit}>
                    <span className={classes.programsHeadWithInfo}>
                      Auto draft with AI
                      <InfoIcon
                        className={classes.rowInfoIcon}
                        tooltip={AUTO_SUBMIT_COLUMN_TOOLTIP}
                      />
                    </span>
                  </span>
                  <span />
                </div>

                {settingsLoading
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={`programs-skeleton-${index}`}
                        className={cx(
                          classes.skeletonProgramsRow,
                          index % 2 === 0 && classes.programsRowAlt
                        )}
                        data-testid={
                          index === 0 ? 'programs-table-skeleton' : undefined
                        }
                      >
                        <span
                          className={cx(
                            classes.skeletonBar,
                            classes.skeletonProgramsName
                          )}
                        />
                        <span
                          className={cx(
                            classes.skeletonBar,
                            classes.skeletonProgramsCount
                          )}
                        />
                        <span
                          className={cx(
                            classes.skeletonBar,
                            classes.skeletonProgramsToggle
                          )}
                        />
                        <span
                          className={cx(
                            classes.skeletonBar,
                            classes.skeletonProgramsChevron
                          )}
                        />
                      </div>
                    ))
                  : null}

                {!settingsLoading &&
                  programRows.map((row, index) => {
                  const templateEligible = supportsFundingTemplates(row.eligible)
                  const count = templateEligible
                    ? templateCountForBenefit(row.eligible!.id)
                    : null
                  const rowAutomation =
                    row.automation ??
                    (row.eligible ? synthesizeAutomation(row.eligible) : null)
                  const autoDraftSupported = supportsAutoDraftAi(row.eligible)
                  const autoSubmitToggleable = canToggleAutoSubmit(
                    row.eligible,
                    count ?? 0,
                    Boolean(row.automation)
                  )
                  // MPOPP (N/A): eligibility always on the card, no expand.
                  // Template programs: eligibility + templates behind expand.
                  const canExpand = templateEligible
                  const expanded =
                    canExpand && expandedProgramKey === row.key
                  const toggleExpand = () => {
                    if (!canExpand) return
                    setExpandedProgramKey(prev =>
                      prev === row.key ? null : row.key
                    )
                    setCriteriaExpandedKey(null)
                    setArchivedExpanded(false)
                  }
                  const criteriaRaw = (
                    row.eligibilityCriteria ||
                    row.eligible?.eligibility_criteria ||
                    ''
                  ).trim()
                  const criteriaOpen = criteriaExpandedKey === row.key
                  const criteriaNeedsMore =
                    criteriaRaw.length > CRITERIA_PREVIEW_CHARS
                  const criteriaShown =
                    criteriaOpen || !criteriaNeedsMore
                      ? criteriaRaw
                      : `${criteriaRaw.slice(0, CRITERIA_PREVIEW_CHARS).trimEnd()}…`
                  const eligibilityMore =
                    criteriaNeedsMore && !criteriaOpen ? (
                      <button
                        type="button"
                        className={classes.criteriaMore}
                        onClick={event => {
                          event.stopPropagation()
                          setCriteriaExpandedKey(row.key)
                        }}
                      >
                        more
                      </button>
                    ) : null
                  /** Template programs — truncated in expand panel. */
                  const eligibilityBlock = (
                    <Typography
                      component="p"
                      className={classes.submissionCriteria}
                      data-testid={`eligibility-criteria-${row.key}`}
                    >
                      <span className={classes.eligibilityLabel}>
                        Eligibility criteria:{' '}
                      </span>
                      {criteriaRaw
                        ? criteriaShown
                        : 'Not provided for this program.'}
                      {eligibilityMore ? <> {eligibilityMore}</> : null}
                    </Typography>
                  )
                  const notApplicableNa = (
                    <Tooltip
                      title={NOT_APPLICABLE_TOOLTIP}
                      arrow
                      placement="top"
                    >
                      <span className={classes.notApplicableNa}>N/A</span>
                    </Tooltip>
                  )
                  const autoSubmitSwitch = (
                    <Switch
                      className={classes.autoSubmitSwitch}
                      checked={Boolean(rowAutomation?.auto_submit)}
                      disabled={!autoSubmitToggleable || !rowAutomation}
                      onChange={() => {
                        if (!rowAutomation) return
                        setPending({
                          key: automationKey(rowAutomation),
                          next: !rowAutomation.auto_submit,
                          automation: rowAutomation,
                        })
                      }}
                      inputProps={{
                        'aria-label': row.label,
                      }}
                    />
                  )
                  const autoSubmitControl = autoDraftSupported
                    ? autoSubmitSwitch
                    : notApplicableNa
                  return (
                    <div
                      key={row.key}
                      className={cx(
                        classes.programsRowGroup,
                        index % 2 === 0 && classes.programsRowAlt
                      )}
                    >
                      {!templateEligible ? (
                        <div className={classes.naProgramBlock}>
                          <span className={classes.naProgramTitle}>
                            {row.label}
                            <InfoIcon
                              className={classes.rowInfoIcon}
                              tooltip={row.description}
                            />
                          </span>
                          <span className={classes.naProgramCount}>
                            {notApplicableNa}
                          </span>
                          <span
                            className={classes.naProgramToggle}
                            onClick={event => event.stopPropagation()}
                            onKeyDown={event => event.stopPropagation()}
                          >
                            {autoSubmitControl}
                          </span>
                          <div className={classes.programEligibilityRow}>
                            <Typography
                              component="p"
                              className={cx(
                                classes.programEligibility,
                                criteriaOpen &&
                                  classes.programEligibilityExpanded
                              )}
                              data-testid={`eligibility-criteria-${row.key}`}
                              title={criteriaRaw || undefined}
                            >
                              <span className={classes.eligibilityLabel}>
                                Eligibility criteria:{' '}
                              </span>
                              {criteriaRaw ||
                                'Not provided for this program.'}
                            </Typography>
                            {eligibilityMore}
                          </div>
                        </div>
                      ) : (
                      <div
                        className={cx(
                          classes.programsRow,
                          canExpand && classes.programsRowClickable
                        )}
                        role={canExpand ? 'button' : undefined}
                        tabIndex={canExpand ? 0 : undefined}
                        aria-expanded={canExpand ? expanded : undefined}
                        onClick={canExpand ? toggleExpand : undefined}
                        onKeyDown={
                          canExpand
                            ? event => {
                                if (
                                  event.key === 'Enter' ||
                                  event.key === ' '
                                ) {
                                  event.preventDefault()
                                  toggleExpand()
                                }
                              }
                            : undefined
                        }
                      >
                        <div className={classes.programCell}>
                          <span className={classes.programName}>
                            {row.label}
                            <InfoIcon
                              className={classes.rowInfoIcon}
                              tooltip={row.description}
                            />
                          </span>
                        </div>
                        <span className={classes.templateCount}>
                          {count == null ? notApplicableNa : String(count)}
                        </span>
                        <span
                          className={classes.autoSubmitCell}
                          onClick={event => event.stopPropagation()}
                          onKeyDown={event => event.stopPropagation()}
                        >
                          {autoSubmitControl}
                        </span>
                        <span className={classes.expandCell}>
                          {canExpand && (
                            <button
                              type="button"
                              className={classes.iconButton}
                              aria-expanded={expanded}
                              aria-label={
                                expanded
                                  ? `Collapse templates for ${row.label}`
                                  : `Expand templates for ${row.label}`
                              }
                              onClick={event => {
                                event.stopPropagation()
                                toggleExpand()
                              }}
                            >
                              <FontAwesomeIcon
                                icon={expanded ? faChevronUp : faChevronDown}
                              />
                            </button>
                          )}
                        </span>
                      </div>
                      )}
                      {expanded && row.eligible && templateEligible ? (
                        <div
                          className={classes.programsRowExpand}
                          data-testid={`program-row-expand-${row.key}`}
                        >
                          {eligibilityBlock}
                          {(() => {
                            const forBenefit = templatesForBenefit(
                              row.eligible.id
                            )
                            const mainRows = forBenefit.filter(
                              t => t.status !== 'archived'
                            )
                            const archivedRows = forBenefit.filter(
                              t => t.status === 'archived'
                            )
                            const renderTemplateRows = (
                              rows: FundingTemplateListItem[]
                            ) =>
                              rows.map(t => (
                                <tr
                                  key={t.template_id}
                                  onClick={() =>
                                    void openEditTemplate(
                                      t.template_id,
                                      row.label
                                    )
                                  }
                                >
                                  <td>
                                    <button
                                      type="button"
                                      className={classes.templateNameLink}
                                      title={
                                        t.name?.trim() || 'Untitled template'
                                      }
                                      onClick={event => {
                                        event.stopPropagation()
                                        void openEditTemplate(
                                          t.template_id,
                                          row.label
                                        )
                                      }}
                                    >
                                      {t.name?.trim() || 'Untitled template'}
                                    </button>
                                  </td>
                                  <td>
                                    {(() => {
                                      const who =
                                        t.created_by?.trim() ||
                                        t.updated_by?.trim() ||
                                        ''
                                      const when =
                                        formatWalletDate(t.created_at) ?? '—'
                                      return who ? `${who} on ${when}` : when
                                    })()}
                                  </td>
                                  <td>
                                    <span
                                      className={cx(
                                        classes.templateStatusPill,
                                        t.status === 'active' &&
                                          classes.templateStatusActive,
                                        t.status === 'disabled' &&
                                          classes.templateStatusDisabled,
                                        t.status === 'archived' &&
                                          classes.templateStatusArchived
                                      )}
                                    >
                                      {t.status === 'disabled'
                                        ? 'Inactive'
                                        : t.status.charAt(0).toUpperCase() +
                                          t.status.slice(1)}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            const hasListContent =
                              mainRows.length > 0 || archivedRows.length > 0
                            const templateColgroup = (
                              <colgroup>
                                <col className="colName" />
                                <col className="colCreated" />
                                <col className="colStatus" />
                              </colgroup>
                            )
                            const showArchivedRows =
                              archivedExpanded && archivedRows.length > 0
                            return (
                              <>
                                {hasListContent ? (
                                  <div className={classes.templatesCard}>
                                    {mainRows.length > 0 ? (
                                      <table
                                        className={
                                          classes.inlineTemplatesTable
                                        }
                                      >
                                        {templateColgroup}
                                        <thead>
                                          <tr>
                                            <th>Template name</th>
                                            <th>Created by</th>
                                            <th>Status</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {renderTemplateRows(mainRows)}
                                        </tbody>
                                      </table>
                                    ) : null}
                                    {archivedRows.length > 0 ? (
                                      <button
                                        type="button"
                                        className={classes.archivedToggle}
                                        onClick={event => {
                                          event.stopPropagation()
                                          setArchivedExpanded(prev => !prev)
                                        }}
                                      >
                                        {archivedRows.length} archived
                                        <FontAwesomeIcon
                                          icon={
                                            archivedExpanded
                                              ? faChevronUp
                                              : faChevronDown
                                          }
                                        />
                                      </button>
                                    ) : null}
                                    {showArchivedRows ? (
                                      <table
                                        className={
                                          classes.inlineTemplatesTable
                                        }
                                      >
                                        {templateColgroup}
                                        {mainRows.length === 0 ? (
                                          <thead>
                                            <tr>
                                              <th>Template name</th>
                                              <th>Created by</th>
                                              <th>Status</th>
                                            </tr>
                                          </thead>
                                        ) : null}
                                        <tbody>
                                          {renderTemplateRows(archivedRows)}
                                        </tbody>
                                      </table>
                                    ) : null}
                                  </div>
                                ) : null}
                                <button
                                  type="button"
                                  className={classes.addTemplateLink}
                                  onClick={event => {
                                    event.stopPropagation()
                                    openCreateTemplate(
                                      row.eligible?.id,
                                      row.label
                                    )
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                  Add a new template
                                </button>
                              </>
                            )
                          })()}
                        </div>
                      ) : null}
                    </div>
                  )
                })}

                {!settingsLoading && programRows.length === 0 && (
                  <Typography component="p" className={classes.programsCopy}>
                    No funding programs configured yet.
                  </Typography>
                )}

                {!settingsLoading && (
                <div className={classes.browseProgramsRow}>
                  <button
                    type="button"
                    className={classes.browseProgramsLink}
                    data-testid="browse-all-programs"
                    aria-haspopup="listbox"
                    aria-expanded={Boolean(browseAnchorEl)}
                    onClick={event => {
                      void openBrowseMenu(event.currentTarget)
                    }}
                  >
                    <FontAwesomeIcon icon={faFileCirclePlus} />
                    Browse all programs to add it
                  </button>
                  <Popover
                    open={Boolean(browseAnchorEl)}
                    anchorEl={browseAnchorEl}
                    onClose={closeBrowseMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: { className: classes.browseProgramsMenu },
                    }}
                  >
                    <div data-testid="browse-programs-menu">
                      <div className={classes.browseProgramsSearchWrap}>
                        <input
                          className={classes.browseProgramsSearch}
                          data-testid="browse-programs-search"
                          placeholder="Search programs"
                          aria-label="Search programs"
                          value={browseSearch}
                          onChange={event =>
                            setBrowseSearch(event.target.value)
                          }
                          autoFocus
                        />
                      </div>
                      <div
                        className={classes.browseProgramsList}
                        role="listbox"
                        aria-label="Sub programs"
                      >
                        {browseTypesLoading ? (
                          <Typography
                            component="p"
                            className={classes.browseProgramsEmpty}
                          >
                            Loading programs…
                          </Typography>
                        ) : browseTypesError ? (
                          <Typography
                            component="p"
                            className={classes.browseProgramsEmpty}
                          >
                            Failed to load programs
                          </Typography>
                        ) : browseFilteredPrograms.length === 0 ? (
                          <Typography
                            component="p"
                            className={classes.browseProgramsEmpty}
                          >
                            {browseSubPrograms.length === 0
                              ? 'No programs available'
                              : 'No matching programs'}
                          </Typography>
                        ) : (
                          browseFilteredPrograms.map(type => {
                            const label = displayFundingProgramLabel(
                              type.program
                            )
                            return (
                              <button
                                key={type.id}
                                type="button"
                                role="option"
                                className={classes.browseProgramsOption}
                                data-testid={`browse-program-option-${type.id}`}
                                onClick={() => addProgramFromBrowse(type)}
                              >
                                {label}
                              </button>
                            )
                          })
                        )}
                      </div>
                    </div>
                  </Popover>
                </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <div className={classes.rightColumn}>
        <div className={classes.viewFundingCard}>
          <button
            type="button"
            className={classes.viewFundingLink}
            data-testid="view-funding-requests"
            onClick={() => navigate('/funding')}
          >
            <FontAwesomeIcon icon={faEye} />
            View your funding requests
          </button>
        </div>
        <div className={classes.sideCard}>
          <div className={classes.sideCardHeader}>
            <span className={classes.iconBox}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography component="h2" className={classes.sideCardTitle}>
              Audit logs
            </Typography>
          </div>
          {auditLog.length > 0 ? (
            <div className={classes.auditScrollWrap}>
              <div ref={auditRef} className={classes.auditList}>
                {[...auditLog].reverse().map((entry, index) => {
                  const time = formatActivityTime(entry.event_at)
                  const meta = [entry.actor, time].filter(Boolean).join(' · ')
                  return (
                    <div key={entry.id}>
                      {index > 0 && <span className={classes.auditDivider} />}
                      <div className={classes.auditItem}>
                        <Typography
                          component="span"
                          className={classes.auditItemText}
                        >
                          {entry.summary}
                        </Typography>
                        {meta && (
                          <Typography
                            component="span"
                            className={classes.auditItemTime}
                          >
                            {meta}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              <ScrollHint scrollRef={auditRef} contentKey={auditLog.length} />
            </div>
          ) : (
            <div className={classes.auditEmpty}>
              <img
                src={AUDIT_WATERMARK_SVG}
                alt=""
                data-testid="audit-watermark"
                className={classes.auditWatermark}
              />
            </div>
          )}
        </div>
      </div>

      <AutoSubmitConfirmDialog
        open={pendingProgram != null}
        title={copy.title}
        bodyLines={copy.bodyLines}
        limitUsd={pendingProgram?.auto_submission_limit_usd}
        onCancel={() => setPending(null)}
        onProceed={() => {
          if (pending) {
            setAutoSubmit(pending.key, pending.next, pending.automation)
          }
          setPending(null)
        }}
      />

    </div>
  )
}

export default FundingSettings
